import { type PropsWithChildren, useEffect } from "react";
import { Navigate, useLocation } from "react-router";

import { usePollTokenExpiration } from "@vega-api/custom/hooks/usePollTokenExpiration";
import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { useInactivityAutoLogout } from "@vega-core/hooks/useInactivityAutoLogout";
import { isF2F, isMSW } from "@vega-core/utils/global";
import { isFalsy } from "@vega-core/utils/isFalsy";

import { OrganizationGuard } from "../../components/OrganisationGuard";
import { useTasksGuard } from "../../context/TasksStateContext/useTasksGuard";
import { CommonPaths } from "../../paths";
import { usePendingTasks } from "../PendingTasks/usePendingTasks";
import { useTxIdCapture } from "../PendingTasks/useTxIdCapture";

import { ErrorBoundary } from "./ErrorBoundary";
import { useSavedRedirectHandler } from "./useSavedRedirect";

export const LPPrivateRoute = ({ children }: PropsWithChildren) => {
  usePollTokenExpiration({ logoutPath: CommonPaths.Logout });
  useInactivityAutoLogout({ logoutPath: CommonPaths.Logout });
  useTxIdCapture();
  usePendingTasks();
  const session = useSession();
  const location = useLocation();

  const { shouldRedirect, shouldShowLoader: isLoadingTasks } = useTasksGuard();

  const isMockEnvironment = isMSW || isF2F;

  if (isFalsy(session.user)) {
    return <Navigate replace to={CommonPaths.Login} />;
  }

  if (isLoadingTasks) {
    return <LoadingSpinner />;
  }

  if (shouldRedirect && !isMockEnvironment) {
    return (
      <Navigate replace state={{ from: location }} to={CommonPaths.Welcome} />
    );
  }

  return (
    <ErrorBoundary isPublicPage={false} locationKey={location.pathname}>
      <OrganizationGuard>
        <RedirectGuard>{children}</RedirectGuard>
      </OrganizationGuard>
    </ErrorBoundary>
  );
};

export const RedirectGuard = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const { handleSavedRedirectOnHome } = useSavedRedirectHandler();

  useEffect(() => {
    handleSavedRedirectOnHome({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  }, [
    location.pathname,
    location.search,
    location.hash,
    handleSavedRedirectOnHome,
  ]);

  return children;
};
